'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdHealthAndSafety,
    MdClose,
    MdCheckCircle,
    MdCancel,
    MdRefresh,
    MdHistory,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getRndsConfigurations,
    deleteRndsConfiguration,
    testRndsConfiguration,
    getRndsConfigurationsByCompany,
    RndsConfiguration,
} from '@/lib/api/health-integrations/rnds-api'
import { ShareableModule } from '@/lib/api/share/share-api'
import { MODULE_LABELS } from '@/hooks/useShareData'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { MdFileDownload } from 'react-icons/md'

export default function RndsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'health_integrations',
        'rnds'
    )

    const [configurations, setConfigurations] = useState<RndsConfiguration[]>(
        []
    )
    const [filteredConfigurations, setFilteredConfigurations] = useState<
        RndsConfiguration[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [configToDelete, setConfigToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [testing, setTesting] = useState<number | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadConfigurations()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = configurations.filter(
                config =>
                    config.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    config.cnes.includes(searchTerm) ||
                    config.cnpj.includes(searchTerm)
            )
            setFilteredConfigurations(filtered)
        } else {
            setFilteredConfigurations(configurations)
        }
    }, [searchTerm, configurations])

    const loadConfigurations = async () => {
        setLoading(true)
        try {
            const response = await getRndsConfigurations()
            if (response.success) {
                setConfigurations(response.result)
                setFilteredConfigurations(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar configurações RNDS'
                )
            }
        } catch (error) {
            toast.error('Erro ao carregar configurações RNDS')
        } finally {
            setLoading(false)
        }
    }

    const handleTest = async (id: number) => {
        setTesting(id)
        try {
            const response = await testRndsConfiguration(id)
            if (response.success && response.result.success) {
                toast.success('Conexao testada com sucesso')
            } else {
                toast.error(
                    response.result?.message || 'Falha no teste de conexão'
                )
            }
        } catch (error) {
            toast.error('Erro ao testar conexão')
        } finally {
            setTesting(null)
        }
    }

    const handleDelete = (id: number) => {
        setConfigToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!configToDelete) return

        setDeleting(true)
        try {
            const response = await deleteRndsConfiguration(configToDelete)
            if (response.success) {
                toast.success('Configuração excluída com sucesso')
                loadConfigurations()
                setShowDeleteModal(false)
                setConfigToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir configuração')
            }
        } catch (error) {
            toast.error('Erro ao excluir configuração')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando configurações RNDS...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <MdHealthAndSafety className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            RNDS/eSUS
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {configurations.length}{' '}
                            {configurations.length === 1
                                ? 'configuração'
                                : 'configurações'}{' '}
                            cadastradas
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() =>
                            navigate('/health-integrations/rnds/transactions')
                        }
                    >
                        <MdHistory className="text-xl" />
                        <span className="hidden sm:inline">Transações</span>
                    </button>
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() => setShowImportModal(true)}
                    >
                        <MdFileDownload className="text-xl" />
                        <span className="hidden sm:inline">Importar</span>
                    </button>
                    {canCreate && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() =>
                                navigate('/health-integrations/rnds/new')
                            }
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Adicionar</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, CNES ou CNPJ..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredConfigurations.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdHealthAndSafety className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma configuração encontrada'
                                : 'Nenhuma configuração RNDS cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Configure sua empresa para enviar dados a Rede Nacional de Dados em Saúde'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/health-integrations/rnds/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar configuração
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {filteredConfigurations.map(config => (
                        <div
                            key={config.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-teal-500/20 to-teal-500/10 rounded-xl flex items-center justify-center border border-teal-500/20">
                                    <MdHealthAndSafety className="text-3xl text-teal-500" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-bold text-base-content break-words">
                                                    {config.name}
                                                </h3>
                                                {config.active ? (
                                                    <span className="badge badge-success badge-sm gap-1">
                                                        <MdCheckCircle className="text-xs" />
                                                        Ativa
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-ghost badge-sm gap-1">
                                                        <MdCancel className="text-xs" />
                                                        Inativa
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-base-content/70 mt-1">
                                                CNPJ: {config.cnpj} | CNES:{' '}
                                                {config.cnes} | UF: {config.uf}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                        <span
                                            className={`badge badge-sm ${config.environment === 'PRODUCTION' ? 'badge-success' : 'badge-warning'}`}
                                        >
                                            {config.environment === 'PRODUCTION'
                                                ? 'Producao'
                                                : 'Homologacao'}
                                        </span>
                                        {config.accessToken && (
                                            <span className="badge badge-outline badge-sm">
                                                Token Ativo
                                            </span>
                                        )}
                                        {config._count && (
                                            <span className="badge badge-outline badge-sm">
                                                {config._count.transactions}{' '}
                                                transações
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2 text-xs text-base-content/60">
                                            <span>
                                                Criada em{' '}
                                                {new Date(
                                                    config.createdAt
                                                ).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() =>
                                                    handleTest(config.id)
                                                }
                                                className="btn btn-sm btn-ghost"
                                                title="Testar conexão"
                                                disabled={testing === config.id}
                                            >
                                                {testing === config.id ? (
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                ) : (
                                                    <MdRefresh className="text-lg" />
                                                )}
                                            </button>
                                            {canUpdate && (
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/health-integrations/rnds/edit/${config.id}`
                                                        )
                                                    }
                                                    className="btn btn-sm btn-ghost"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() =>
                                                        handleDelete(config.id)
                                                    }
                                                    className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.RNDS_CONFIG}
                moduleLabel={MODULE_LABELS[ShareableModule.RNDS_CONFIG]}
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response =
                        await getRndsConfigurationsByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: RndsConfiguration) => (
                    <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-base-content/60">
                            CNPJ: {item.cnpj} | CNES: {item.cnes} | UF:{' '}
                            {item.uf}
                            {item.active && (
                                <span className="badge badge-success badge-xs ml-2">
                                    Ativa
                                </span>
                            )}
                        </div>
                    </div>
                )}
                getItemId={(item: RndsConfiguration) => item.id}
                getItemName={(item: RndsConfiguration) => item.name}
                onImportComplete={() => loadConfigurations()}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setConfigToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir configuração"
                message="Tem certeza de que deseja excluir esta configuração RNDS?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
